// PermissionsPage — RBAC matrix viewer + live editor (director + admin only).
// director/administrator columns are fixed (always full access); the other
// 4 roles are click-to-cycle editable, no deploy needed.
import { useEffect, useState } from "react";
import { Loader2, Check, X, Eye, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CELL = { E: { bg: "#3F7D3A", fg: "#fff", icon: Check }, V: { bg: "#FBEFE5", fg: "#9F4212", icon: Eye }, "-": { bg: "#F0EDE8", fg: "#9A968F", icon: X } };
const CYCLE = { "-": "V", "V": "E", "E": "-" };

export default function PermissionsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    useEffect(() => {
        api.get("/permissions/matrix").then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Cannot load matrix"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>;
    if (!data) return null;

    const editableRoles = new Set(data.editable_roles || []);

    async function cycleCell(roleId, module, current) {
        const next = CYCLE[current];
        const key = `${roleId}:${module}`;
        setSavingKey(key);
        setData((d) => ({ ...d, permissions: { ...d.permissions, [roleId]: { ...d.permissions[roleId], [module]: next } } }));
        try {
            const { data: updated } = await api.patch("/permissions/matrix", { role: roleId, module, value: next });
            setData(updated);
            toast.success(`${module} · ${roleId.replace("_", " ")} → ${next === "E" ? "View + Edit" : next === "V" ? "View only" : "No access"}`);
        } catch (e) {
            setData((d) => ({ ...d, permissions: { ...d.permissions, [roleId]: { ...d.permissions[roleId], [module]: current } } }));
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update permission");
        }
        setSavingKey(null);
    }

    return (
        <div data-testid="permissions-page" className="space-y-5">
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                    style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Admin</span>
                <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Permissions</h2>
                <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                    6 roles · {data.modules.length} modules · server-enforced via FastAPI dependency. Click a cell to change it — takes effect immediately, no deploy needed.
                </p>
            </div>

            <div className="flex items-center gap-4 text-[12px] flex-wrap">
                {Object.entries(CELL).map(([k, v]) => {
                    const Icon = v.icon;
                    return (
                        <div key={k} className="flex items-center gap-1.5">
                            <span className="inline-grid place-items-center w-5 h-5 rounded" style={{ background: v.bg, color: v.fg }}><Icon size={11} /></span>
                            <span className="text-[#6F6A64]">{k === "E" ? "View + Edit" : k === "V" ? "View only" : "No access"}</span>
                        </div>
                    );
                })}
                <div className="flex items-center gap-1.5">
                    <span className="inline-grid place-items-center w-5 h-5 rounded bg-[#F0EDE8] text-[#9A968F]"><Lock size={10} /></span>
                    <span className="text-[#6F6A64]">Fixed (Director/Administrator)</span>
                </div>
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead className="bg-[#F5F3F0]">
                        <tr>
                            <th className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6F6A64] font-semibold sticky left-0 bg-[#F5F3F0] z-10">Module</th>
                            {data.roles.map((r) => (
                                <th key={r.id} data-testid={`permissions-col-${r.id}`}
                                    className="px-2 py-3 text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap"
                                    style={{ color: r.colour }}>
                                    {r.label.replace("Priority Risk ", "")}
                                    {!editableRoles.has(r.id) && <Lock size={9} className="inline ml-1 -mt-0.5" style={{ color: "#9A968F" }} />}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.modules.map((m) => (
                            <tr key={m} data-testid={`permissions-row-${m}`} className="border-t border-[#F0EDE8]">
                                <td className="px-3 py-2 font-semibold text-[#1F1F1F] sticky left-0 bg-white">{m}</td>
                                {data.roles.map((r) => {
                                    const v = data.permissions[r.id]?.[m] || "-";
                                    const cfg = CELL[v];
                                    const Icon = cfg.icon;
                                    const editable = editableRoles.has(r.id);
                                    const key = `${r.id}:${m}`;
                                    const isSaving = savingKey === key;
                                    return (
                                        <td key={r.id} className="px-2 py-2 text-center">
                                            <button
                                                type="button"
                                                disabled={!editable || isSaving}
                                                onClick={() => cycleCell(r.id, m, v)}
                                                data-testid={`permissions-cell-${r.id}-${m}`}
                                                title={editable ? "Click to change" : "Fixed — always full access"}
                                                className={`inline-grid place-items-center w-7 h-7 rounded transition-transform ${editable ? "cursor-pointer hover:scale-110 active:scale-95" : "cursor-not-allowed"}`}
                                                style={{ background: cfg.bg, color: cfg.fg, opacity: isSaving ? 0.5 : 1 }}
                                            >
                                                {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Icon size={13} />}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
